<template>
  <v-card :style="fillHeight ? 'height: 100%;' : undefined">
    <v-data-table
        :headers="headers"
        :items="items"
        :options.sync="options"
        :search="localSearch"
        :show-select="selectable"
        :loading="isLoading"
        :item-key="uniqueColumn"
        :single-select="!multiSelect"
        :server-items-length.sync="totalItems"
        :value="selected"
        @input="$emit('update:selected', $event)"
        @toggle-select-all="selectAllHandler"
        @click:row="rowClickHandler"
    >

      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title class="font-weight-bold mr-5">{{ title }}</v-toolbar-title>

          <v-dialog v-if="actions.length > 0" v-model="dialogCreate" max-width="500px">

            <template v-slot:activator="{ on }" v-if="actions.includes('create')">
              <v-btn color="primary mr-5" v-on="on" @click="createItem()" :fab="!customCreateDialogText" :small="!customCreateDialogText">
                <template v-if="customCreateDialogText">
                  {{ customCreateDialogText }}
                </template>
                <template v-else>
                  <v-icon dark>mdi-plus</v-icon>
                </template>
              </v-btn>
            </template>

            <v-card>
              <v-card-title>
                <span class="headline">
                  <!--
                  @slot Title of the creation dialog
                  @binding None
                  -->
                  <slot name="create-form-title"></slot>
                </span>
              </v-card-title>

              <v-card-text>
                <!--
                @slot Fields for the creation form, which should be bound the the item object
                @binding None
                -->
                <v-form ref="formCreate" v-model="valid">
                  <slot name="create-form-content"></slot>
                </v-form>
              </v-card-text>

              <v-card-actions>
                <v-spacer></v-spacer>
                <!--
                @slot Save and cancel buttons. Try to avoid messing with these as they are required for fired events.
                @binding None
                -->
                <slot name="create-form-actions">
                  <Button text @click="closeDialog">Cancel</Button>
                  <Button primary @click="saveDialog" :disabled="!valid">{{createButtonText}}</Button>
                </slot>
              </v-card-actions>
            </v-card>

          </v-dialog>

          <v-dialog v-if="actions.length > 0" v-model="dialogUpdate" max-width="500px">

            <v-card>
              <v-card-title>
                <span class="headline">
                  <!--
                  @slot Title of the creation dialog
                  @binding None
                  -->
                  <slot name="update-form-title"></slot>
                </span>
              </v-card-title>

              <v-card-text>
                <!--
                @slot Fields for the creation form, which should be bound the the item object
                @binding None
                -->
                <v-form ref="formUpdate" v-model="valid">
                  <slot name="update-form-content"></slot>
                </v-form>
              </v-card-text>

              <v-card-actions>
                <v-spacer></v-spacer>
                <!--
                @slot Save and cancel buttons. Try to avoid messing with these as they are required for fired events.
                @binding None
                -->
                <slot name="create-form-actions">
                  <Button text @click="closeDialog">Cancel</Button>
                  <Button primary @click="updateDialog" :disabled="!valid">{{updateButtonText}}</Button>
                </slot>
              </v-card-actions>
            </v-card>

          </v-dialog>

          <Column cols="5" lg="3">
            <v-text-field v-if="searchable"
                          v-model="localSearch"
                          append-icon="mdi-magnify"
                          label="Search"
                          single-line
                          hide-details
                          color="primary"
            ></v-text-field>
          </Column>

          <slot name="header-slot"></slot>

          <!-- TODO @JJ 2020-10-26 remove this hardcoded nonsense -->
          <DownloadAnchor v-if="downloadCSV" :url="downloadCSV_url" :disabled="isLoading" :filter-data="postData"
                          class="ml-2">CSV
          </DownloadAnchor>
          <DownloadAnchor v-if="downloadTSV" :url="downloadTSV_url" :disabled="isLoading" :filter-data="postData"
                          class="ml-2">TSV
          </DownloadAnchor>
          <DownloadAnchor v-if="downloadXLSX" :url="downloadXLSX_url" :disabled="isLoading" :filter-data="postData"
                          class="ml-2">Excel
          </DownloadAnchor>

          <Spacer v-if="actions.includes('create')"></Spacer>

        </v-toolbar>
      </template>

      <template v-if="multiSelect" v-slot:[`header.data-table-select`]="{ props, on }">
        <div class="d-flex">
          <v-simple-checkbox
              :value="props.value"
              :indeterminate="props.indeterminate"
              v-on="on"
              :ripple="false"
          >
          </v-simple-checkbox>
          <v-menu
              bottom
              offset-y
              :close-on-content-click="false"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn v-bind="attrs" v-on="on" text icon small>
                <v-icon>
                  mdi-dots-vertical
                </v-icon>
              </v-btn>
            </template>

            <v-list
                subheader
                two-line
                flat
            >
              <v-subheader>Select all behavior</v-subheader>
              <v-list-item-group
                  v-model="selectAllSettings"
              >
                <v-list-item>
                  <template v-slot:default="{ active }">
                    <v-list-item-action>
                      <v-checkbox :input-value="active"></v-checkbox>
                    </v-list-item-action>

                    <v-list-item-content>
                      <v-list-item-title>All Pages</v-list-item-title>
                      <v-list-item-subtitle>Select all effects all available pages</v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-list-item>

                <v-list-item>
                  <template v-slot:default="{ active }">
                    <v-list-item-action>
                      <v-checkbox :input-value="active"></v-checkbox>
                    </v-list-item-action>

                    <v-list-item-content>
                      <v-list-item-title>Current Page</v-list-item-title>
                      <v-list-item-subtitle>Select all effects only the current visible page</v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </v-menu>
        </div>
      </template>

      <template v-slot:[`item.data-table-select`] v-if="inspectBtn">
        <v-btn text icon>
          <v-icon>mdi-eye</v-icon>
        </v-btn>
      </template>

      <template v-if="actions.includes('edit') || actions.includes('delete')" v-slot:[`item.action`]="{ item }">
        <div class="d-flex">
          <v-btn v-if="actions.includes('edit')" :disabled="!itemEditingAllowed(item)" text icon @click.stop="editItem(item)">
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>

          <v-btn v-if="actions.includes('delete')" :disabled="!itemDeletionAllowed(item)" text icon @click.stop="deleteItem(item)">
            <v-icon small>mdi-delete</v-icon>
          </v-btn>
        </div>
      </template>

      <!-- this loads the dynamic render functions which may be passed as template -->
      <template v-for="column in customColumns" v-slot:[buildSlotName(column)]="{ item }">
        <!-- @slot Custom rendering of specific columns -->
        <slot :name="buildSlotName(column)" v-bind:item="item"
              :class="key === inspectedRow ? 'highlight-row' : ''"></slot>
      </template>

      <template v-slot:loading>
        <Logo animated width="70" class="mt-5"></Logo>
        <p>Loading data... Please wait</p>
      </template>

      <template v-if="appendTotal && total && items.length" v-slot:[`body.append`]="{ headers }">
        <tr>
          <th>
            Total
          </th>
          <th v-for="col in headers.slice(1)" :key="col.value">
            <span v-if="col['appendTotal']" style="float: right">{{ prettyPrintNumber(total) }}</span>
          </th>
        </tr>
      </template>

    </v-data-table>

  </v-card>
</template>

<script>
import axios from 'axios';
import moment from 'moment';

/**
 * This datatable is used to directly interact with REST endpoints from various microservices.
 */
export default {
  name: 'DataTableFlyfood',
  props: {
    /**
     * Displayed title of the datatable
     */
    title: String,
    /**
     * Relative or absolute endpoint (leading to the express.js backend) of the REST resource to be displayed
     */
    endpoint: String,
    /**
     * Send optional post data to the endpoint
     */
    postData: {type: Object, default: null},
    /**
     * Headers of the columns. Pass a display name as 'text' and the JSON object name as 'value'. Pass an 'action' header if you want to display CRUD buttons
     */
    headers: Array,
    /**
     * Show select fields for column inspection
     */
    selectable: {type: Boolean, default: false},
    /**
     * Activate selection of multiple rows
     */
    multiSelect: {type: Boolean, default: false},
    /**
     * Enable the create, edit, delete item feature
     * @values create,edit,delete
     */
    actions: {type: Array, default: () => []},
    /**
     * Column, which content should be used as unique identifier; pass the same value as the 'value' of the respective header
     */
    uniqueColumn: String,
    /**
     * Display the inspect button (requires selectable)
     */
    inspectBtn: {type: Boolean, default: false},
    /**
     * Object used for adding/editing table elements; bound to the separate form elements in the injected form slots
     */
    item: Object,
    /**
     * Enable server side paging
     */
    pageable: {type: Boolean, default: false},
    /**
     * Enable download CSV button
     */
    downloadCSV: {type: Boolean, default: false},
    /**
     * Download URL for CSV button
     */
    downloadCSV_url: {type: String},
    /**
     * Enable download TSV button
     */
    downloadTSV: {type: Boolean, default: false},
    /**
     * Download URL for TSV button
     */
    downloadTSV_url: {type: String},
    /**
     * Enable download XLSX button
     */
    downloadXLSX: {type: Boolean, default: false},
    /**
     * Download UDL for XLSX button
     */
    downloadXLSX_url: {type: String},
    /**
     * Whether a search field appears or not
     */
    searchable: {type: Boolean, default: false},
    /**
     * Display sum row with total attribute found in items
     */
    appendTotal: {type: Boolean, default: false},
    /**
     * Array of data
     */
    items: {type: Array, default: () => []},
    loading: {type: Boolean, default: false},
    /**
     * decides which http method is used for data pulling
     */
    method: {type: String, default: 'get'},
    /**
     * decides which fields of the Item should be ignored while clearing
     */
    ignore_fields: {type: Array, default: () => ['author']},

    /**
     * Array of selected items
     */
    selected: {type: Array, default: () => []},
    /**
     * Used as a hacky event listener to reload data
     */
    updateDatatable: null,
    /**
     * Fill available height
     */
    fillHeight: {type: Boolean, default: false},
    search: {type: String, default: ''},
    customCreateDialogText: null,
    createButtonText: {type: String, default: 'Save'},
    updateButtonText: {type: String, default: 'Save'},
    itemEditingAllowed: {type: [Function], default: () => true},
    itemDeletionAllowed: {type: [Function], default: () => true},
    initialOptions: {}
  },
  data() {
    return {
      //search: '',
      isLoading: false,
      totalItems: -1,
      dialogCreate: false,
      dialogUpdate: false,
      editedIndex: -1,
      inspectedRow: null,
      valid: false,
      total: '',
      selectAllSettings: 0,
      values: [],
      localSearch: '',
      options: this.initialOptions
    }
  },
  methods: {
    async deleteItem(item) {
      const index = this.items.indexOf(item);
      // Remove query parameters
      const deleteEndPoint = this.endpoint.split('?')[0];
      confirm('Are you sure you want to delete this item?') &&
      await axios.delete(deleteEndPoint + '/' + this.items[index][this.uniqueColumn]);
      await this.populate();
    },
    clearObject(object) {
      for (let prop in object) {
        if (Object.prototype.hasOwnProperty.call(object, prop)) {
          if (this.ignore_fields.indexOf(prop) < 0) {
            if (typeof object[prop] === 'string' || typeof object[prop] === 'number') {
              object[prop] = '';
            } else if (typeof object[prop] === 'boolean') {
              object[prop] = false;
            } else if (typeof object[prop] === 'object') {
              this.clearObject(object[prop]);
            }
          }
        }
      }
    },
    createItem() {
      this.resetValidation();
      this.$emit('openDialog');
      this.clearObject(this.item);
      this.dialogCreate = true;
    },
    editItem(item) {
      // reactive two-way binding
      this.$emit('update:item', Object.assign({}, item));
      this.resetValidation();
      this.dialogUpdate = true;
    },
    async saveDialog() {
      this.$emit('saveDialog');
      this.closeDialog();
      await axios.post(this.endpoint, Object.assign({}, this.item));
      await this.populate();
    },
    async updateDialog() {
      this.$emit('updateDialog');
      this.closeDialog();
      await axios.put(this.endpoint, Object.assign({}, this.item));
      await this.populate();
    },
    closeDialog() {
      this.dialogCreate = false;
      this.dialogUpdate = false;
      this.$emit('closeDialog');
    },
    resetValidation() {
      if (this.$refs.formCreate) {
        this.$refs.formCreate.resetValidation();
      }
      if (this.$refs.formUpdate) {
        this.$refs.formUpdate.resetValidation();
      }
    },
    async populate() {
      if (!this.endpoint) {
        this.$emit('update:items', [...this.items]);
      } else {
        this.isLoading = true;
        console.info(`Loading DataTable... ${this.endpoint} (SSR = ${this.pageable})`);
        const {data} = await axios({
          method: this.method,
          url: this.endpoint + this.pageableQuery(),
          data: this.postData
        });
        let content = data;
        if (this.pageable) {
          content = data.content;
          this.totalItems = data.totalElements;
        }
        if (content && content[0]) {
          this.total = content[0].total;
        }
        this.$emit('update:items', [...content]);
        this.isLoading = false;
      }
    },
    parseDate(date) {
      let pretty_date = moment(date).format('YYYY-MM-DD hh:mm:ss.SSS');
      if (pretty_date === 'Invalid date') pretty_date = '';
      return pretty_date;
    },
    prettyPrintNumber(number) {
      // noinspection JSCheckFunctionSignatures
      return number.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
    },
    /**
     * Dynamically build the slot name. e.g. item.company if the company column is to be rendered
     */
    buildSlotName(column) {
      return "item." + column.value;
    },

    pageableQuery() {
      if (this.pageable) {
        let sort = '';
        if (this.headers.map(a => a.value).some(el => this.options.sortBy.indexOf(el) >= 0)
            && this.options.sortBy[0]) {
          sort = `${this.options.sortBy[0]},${this.options.sortDesc[0] ? 'desc' : 'asc'}`;
        }
        const separator = this.endpoint.includes('?') ? '&' : '?';
        return `${separator}page=${this.options.page - 1}&size=${this.options.itemsPerPage}&sort=${sort}`;
      }
      return '';
    },

    rowClickHandler(item, props) {
      if (this.selectable) {
        props.select(!props.isSelected);
        if (this.inspectBtn) {
          this.inspectedRow = props.item
        }
      }
    },
    selectAllHandler(props) {
      if (this.selectAllSettings !== 1) {
        if (props.value) {
          this.$emit('update:selected', [...this.items]);
        } else {
          this.$emit('update:selected', []);
        }
      }
    }
  },
  computed: {
    /**
     * Only return columns with which have specified a render component
     */
    customColumns() {
      return this.headers.filter(column => this.$scopedSlots[this.buildSlotName(column)]);
    }
  },
  mounted() {
    this.isLoading = this.loading;

    if (this.actions.includes('edit') || this.actions.includes('delete')) {
      this.$emit('update:headers', [...this.headers, {
        text: 'Actions', value: 'action', sortable: false, width: 1
      }]);
    }

    if (!this.pageable) {
      this.populate();
    }
  },
  watch: {
    search(val) {
      this.localSearch = val;
    },
    localSearch(val) {
      this.$emit("update:search", val);
    },
    dialog(val) {
      val || this.closeDialog()
    },
    loading() {
      this.isLoading = this.loading;
    },
    endpoint() {
      this.populate();
    },
    postData() {
      this.populate();
    },
    options() {
      if (this.pageable) {
        this.populate();
      }
    },
    updateDatatable() {
      this.populate();
    }
    // selected() {
    //   this.values = this.selected;
    // }
  }
}

</script>

<style lang="scss" scoped>
::v-deep tr.v-data-table__selected {
  background: hsl(81, 67%, 85%) !important;

  &:hover {
    background: hsl(81, 67%, 80%) !important;
  }
}
</style>